import { AxiosResponse } from "axios";
import httpDao from "../infrastructure/httpDao";
import { localAuthRepository } from "./localAuthRepository";
import { isTenantStaffRole } from "@/lib/staffPermissions";

// /auth/staff/login のレスポンス。
// - 信頼済みデバイス: accessToken / refreshToken を返す
// - 新しい端末: twoFactorRequired=true / challengeId を返す
export interface StaffLoginResponse {
  accessToken?: string;
  refreshToken?: string;
  twoFactorRequired?: boolean;
  challengeId?: string;
}

// /auth/staff/2fa/verify のレスポンス。
// rememberDevice=true のとき trustedDeviceToken を併せて返す。
export interface TwoFactorVerifyResponse {
  accessToken: string;
  refreshToken: string;
  trustedDeviceToken?: string;
}

export const authRepository = {
  // ログイン状態チェック（ローカルストレージからme情報を取得）
  fetchIam: async (): Promise<AxiosResponse> => {
    // まずローカルストレージから取得を試みる
    const cachedUserInfo = localAuthRepository.getUserInfo();
    // 担当範囲や組織を持たない旧キャッシュは /me で更新し、画面の状態を分離する。
    if (
      cachedUserInfo?.facilityScope &&
      Object.prototype.hasOwnProperty.call(cachedUserInfo, "organizationId") &&
      Object.prototype.hasOwnProperty.call(cachedUserInfo, "platformRole") &&
      // プロフィール写真導入前のキャッシュは avatarUrl 自体を持たない。
      // 一度 /me を再取得し、以降は null も有効なキャッシュ値として扱う。
      Object.prototype.hasOwnProperty.call(cachedUserInfo, "avatarUrl") &&
      (!isTenantStaffRole(cachedUserInfo.role) ||
        cachedUserInfo.effectivePermissions !== undefined)
    ) {
      // キャッシュされたデータを返す
      return {
        data: cachedUserInfo,
        status: 200,
        statusText: "OK",
        headers: {},
        config: {},
      } as AxiosResponse;
    }

    // キャッシュにない場合のみAPIから取得
    const res = await httpDao.get("/me");
    // 取得したデータをローカルストレージに保存
    localAuthRepository.setUserInfo(res.data);
    return res;
  },

  // ログイン状態チェック（強制的にAPIから取得）
  fetchIamForce: async (): Promise<AxiosResponse> => {
    const res = await httpDao.get("/me");
    // 取得したデータをローカルストレージに保存
    localAuthRepository.setUserInfo(res.data);
    return res;
  },

  // API からme情報を取得する。
  // access token が期限切れ/未保存の場合の refresh と再試行は
  // httpDao の response interceptor が一度だけ実行する。
  fetchIamWithRetry: async (): Promise<AxiosResponse> => {
    try {
      const res = await httpDao.get("/me");
      localAuthRepository.setUserInfo(res.data);
      return res;
    } catch (error: any) {
      // /me が 404 の場合はユーザー削除済みとみなし、ローカル状態を破棄する。
      if (error.response?.status === 404) {
        localAuthRepository.clearAll();
        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/" &&
          !window.location.pathname.startsWith("/join")
        ) {
          window.location.href = "/";
        }
      }
      throw error;
    }
  },

  // ログアウト
  // サーバー側でリフレッシュトークンを失効させ、httpOnly Cookie を削除する。
  // API 呼び出しが失敗してもローカルのログアウト処理は継続してよい
  // （Cookie が残ってもトークン自体は期限切れ/無効なら使えない）。
  logout: async (): Promise<void> => {
    try {
      await httpDao.post("/auth/staff/logout", {});
    } catch {
      // ネットワークエラー等でもローカルログアウトは継続する
    }
  },

  recordModeSwitch: async (params: {
    fromMode: "tenant" | "platform";
    toMode: "tenant" | "platform";
  }): Promise<void> => {
    await httpDao.post("/auth/staff/mode-switch", params);
  },

  // ログイン（1段階目）
  // 信頼済みデバイストークンが保存されていれば添付して 2FA をスキップさせる。
  // レスポンスは { accessToken, refreshToken }（2FA不要）または
  // { twoFactorRequired: true, challengeId }（新しい端末）のいずれか。
  login: async (params: {
    email: string;
    password: string;
  }): Promise<AxiosResponse<StaffLoginResponse>> => {
    const trustedDeviceToken = localAuthRepository.getTrustedDeviceToken();
    const res = await httpDao.post<StaffLoginResponse>("/auth/staff/login", {
      ...params,
      ...(trustedDeviceToken ? { trustedDeviceToken } : {}),
    });
    return res;
  },

  // 二段階認証の検証（2段階目）
  // 成功で JWT を発行。rememberDevice=true のとき trustedDeviceToken を併せて返す。
  verifyTwoFactor: async (params: {
    challengeId: string;
    code: string;
    rememberDevice: boolean;
  }): Promise<AxiosResponse<TwoFactorVerifyResponse>> => {
    const res = await httpDao.post<TwoFactorVerifyResponse>(
      "/auth/staff/2fa/verify",
      params
    );
    return res;
  },

  // 二段階認証コードの再送
  resendTwoFactor: async (params: {
    challengeId: string;
  }): Promise<AxiosResponse<{ message: string }>> => {
    const res = await httpDao.post<{ message: string }>(
      "/auth/staff/2fa/resend",
      params
    );
    return res;
  },

  // 未ログイン状態のパスワード再設定メールを要求する。
  // APIはアカウントの存在有無にかかわらず同じレスポンスを返す。
  requestPasswordReset: async (params: {
    email: string;
  }): Promise<AxiosResponse<{ message: string }>> => {
    return httpDao.post<{ message: string }>(
      "/auth/staff/password-reset/request",
      params
    );
  },

  // メールの一度限りトークンを使って新しいパスワードを設定する。
  resetPassword: async (params: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<AxiosResponse<{ message: string }>> => {
    return httpDao.post<{ message: string }>(
      "/auth/staff/password-reset/confirm",
      params
    );
  },
};
